import React, { useEffect } from "react";
import { Draggable } from "@fullcalendar/interaction";
import { Typography, Grid, CircularProgress, Box } from "@mui/material";
import { AppointmentCard } from "../../components/ScheduleOverview/UnscheduledAppointments";
import { TopLevelPaper } from "../../themes/StyledComponents";
import moment from "moment";
import { PatientDto, TimeSlotDto, TherapyRxDto } from "../../api-client";
import { useCancelledAppointments } from "../../stores/Appointment/appointment.queries";
import { useLocationId } from "../../stores/User/User.hooks";
import { CalendarContext } from "../../stores/Calendar/calendar.provider";
import useStore from "../../helpers/useStore";
import { CalendarCommands } from "../../stores/Calendar/calendar.commands";
import { adaptAppointmentToDraggableEvent, ClientTimeSlot, adaptAppointmentToClientTimeSlot } from "./controller/adapters";
import { observer } from "mobx-react";


const CancellationDrawer = observer(() => {
  // Fetch data using react-query hooks
  const locationId = useLocationId();
  const { data: cancelledAppointments = [], isLoading, error } = useCancelledAppointments(locationId);

  const CalendarStore = useStore(CalendarContext);
  const { onEventContentClick } = CalendarCommands(CalendarStore);


  // Helper function to convert minutes to HH:mm:ss format
  const convertMinutesToTimeString = (minutes: number) => {
    const duration = moment.duration(minutes, "minutes");
    return moment.utc(duration.asMilliseconds()).format("HH:mm:ss");
  };

  const handleEventDidMount = (timeSlot: ClientTimeSlot) => {
    return onEventContentClick && onEventContentClick(timeSlot);
  };

  useEffect(() => {
    // Only initialize draggable if we have cancelled appointments and the element exists
    if (cancelledAppointments && cancelledAppointments.length > 0) {
      const containerEl = document.getElementById("external-cancelled-events");
      
      if (containerEl) {
        new Draggable(containerEl, {
          itemSelector: ".fc-event",
          eventData: (eventEl) => {
            const eventDataJson = JSON.parse(eventEl.getAttribute("data-event")!);
            const eventData = {
              title: eventEl.innerText,
              duration: eventDataJson["duration"],
              id: eventDataJson["id"],
              patient: eventDataJson["patient"],
              timeSlots: eventDataJson["timeSlots"],
              frequency: eventDataJson["frequency"],
            };
            return eventData;
          },
        });
      }
    }
  }, [cancelledAppointments]); // Re-run when cancelled appointments change

  // Show loading spinner while data is being fetched
  if (isLoading) {
    return (
      <TopLevelPaper>
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
        </Box>
      </TopLevelPaper>
    );
  }

  // Show empty state if no cancelled appointments
  if (!cancelledAppointments || cancelledAppointments.length === 0) {
    return (
      <TopLevelPaper>
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <Typography variant="body2" color="textSecondary">
            Keine stornierten Termine gefunden
          </Typography>
        </Box>
      </TopLevelPaper>
    );
  }

  return (
    <TopLevelPaper>
      <Grid container direction="column" sx={{ display: "flex" }}>
        {cancelledAppointments.length}
        <Grid item>
          <Grid
            container
            direction="column"
            spacing={2}
            id="external-cancelled-events"
            className="draggable-div"
            sx={{ overflowX: "auto", flexWrap: "nowrap", paddingBottom: 1 }}
          >
            {cancelledAppointments.map((cancelledAppointment) => {
              const { appointment, patient } = cancelledAppointment;

              return (
                <div
                  key={appointment.id}
                  onClick={() => {
                    const timeSlot = adaptAppointmentToClientTimeSlot(appointment, patient);
                    handleEventDidMount(timeSlot);
                  }}
                  className="fc-event fc-h-event fc-daygrid-event fc-daygrid-block-event"
                  data-event={JSON.stringify(
                    adaptAppointmentToDraggableEvent(appointment, patient)
                  )}
                >
                  <Typography variant="inherit" style={{ marginBottom: "2px", fontWeight: "bold" }}>
                    {patient?.firstName} {patient?.lastName}
                  </Typography>
                  <AppointmentCard appointment={appointment} />
                </div>
              );
            })}
          </Grid>
        </Grid>
      </Grid>
    </TopLevelPaper>
  );
});

export default CancellationDrawer;
